import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, MessageSquare, Mic, CheckCircle } from 'lucide-react';
/**
 * PreviewImage Component
 *
 * This component is designed to be captured as a GIF for social media previews.
 * Dimensions: 1200x630px (standard OG image size)
 *
 * To generate the GIF:
 * 1. Navigate to /preview route
 * 2. Use a screen recording tool to capture the animation
 * 3. Convert to GIF using a tool like ezgif.com or ffmpeg
 * 4. Upload the GIF and use the URL in meta tags
 */
const PreviewImage = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const steps = [{
      duration: 2000
    }, {
      duration: 1500
    } // Show success
    ];
    const timer = setTimeout(() => {
      setStep(prev => (prev + 1) % steps.length);
    }, steps[step].duration);
    return () => clearTimeout(timer);
  }, [step]);
  return <div className="w-[1200px] h-[630px] bg-white relative overflow-hidden flex items-center justify-center" data-id="element-855">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" data-id="element-856"></div>

      {/* Gradient Blob */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl" data-id="element-857"></div>

      <div className="relative z-10 w-full h-full flex items-center justify-center px-16" data-id="element-858">
        <div className="w-full max-w-5xl" data-id="element-859">
          {/* Logo */}
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="absolute top-8 left-16" data-id="element-860">
            <img src="https://cdn.magicpatterns.com/uploads/fMJnv7yQ9EawQ6b54Tnvxq/FWLogo.png" alt="FluidWeb" className="h-10" data-id="element-861" />
          </motion.div>

          {/* Main Content */}
          <div className="flex items-center justify-between gap-12" data-id="element-862">
            {/* Left: Headline */}
            <div className="flex-1" data-id="element-863">
              <motion.div initial={{
              opacity: 0,
              x: -30
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-864">
                <h1 className="text-5xl font-bold text-gray-900 leading-tight mb-4" data-id="element-865">
                  Turn Your App Into an{' '}
                  <span className="text-orange-500" data-id="element-866">AI App</span>
                </h1>
                <p className="text-xl text-gray-600 mb-6" data-id="element-867">
                  The AI UX layer for your product. Voice, chat, and intelligent
                  UI components.
                </p>
                <div className="flex items-center gap-3 text-sm text-gray-500" data-id="element-868">
                  <div className="flex items-center gap-1" data-id="element-869">
                    <div className="w-2 h-2 bg-orange-500 rounded-full" data-id="element-870"></div>
                    <span data-id="element-871">React SDK</span>
                  </div>
                  <div className="w-1 h-1 bg-gray-300 rounded-full" data-id="element-872"></div>
                  <span data-id="element-873">TypeScript Ready</span>
                  <div className="w-1 h-1 bg-gray-300 rounded-full" data-id="element-874"></div>
                  <span data-id="element-875">Zero Config</span>
                </div>
              </motion.div>
            </div>

            {/* Right: Animated Demo */}
            <div className="flex-1" data-id="element-876">
              <AnimatePresence mode="wait" data-id="element-877">
                {step === 0 && <motion.div key="code" initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="bg-gray-900 rounded-xl p-6 border border-gray-800" data-id="element-878">
                    <div className="flex items-center gap-2 mb-4" data-id="element-879">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-880"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-881"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-882"></div>
                      <span className="ml-2 text-gray-500 text-xs" data-id="element-883">
                        App.tsx
                      </span>
                    </div>
                    <div className="font-mono text-sm space-y-1" data-id="element-884">
                      <div className="text-purple-400" data-id="element-885">
                        import <span className="text-yellow-300" data-id="element-886">FluidWeb</span>
                      </div>
                      <div className="text-gray-500" data-id="element-887">
                        {' '}
                        // Add AI to your app
                      </div>
                      // Add AI to your app
                    </div>
                    <div className="text-blue-400" data-id="element-888">
                      {'<'}FluidWeb tools={'{[...]}'}
                      <span className="text-white" data-id="element-889"> /{'>'}</span>
                    </div>
                  </motion.div>}

                {step === 1 && <motion.div key="voice" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -20
              }} className="bg-white rounded-xl p-6 border border-gray-200 shadow-xl" data-id="element-890">
                    <div className="flex items-center gap-3 mb-4" data-id="element-891">
                      <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center" data-id="element-892">
                        <Mic className="w-6 h-6 text-white" data-id="element-893" />
                      </div>
                      <div data-id="element-894">
                        <div className="text-sm font-semibold text-gray-900" data-id="element-895">
                          Voice Input
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-896">
                          Listening...
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700" data-id="element-897">
                      "Create a new vendor called Acme Corp"
                    </div>
                  </motion.div>}

                {step === 2 && <motion.div key="chat" initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -20
              }} className="bg-white rounded-xl p-6 border border-gray-200 shadow-xl space-y-3" data-id="element-898">
                    <div className="flex items-center gap-2 mb-3" data-id="element-899">
                      <MessageSquare className="w-5 h-5 text-orange-500" data-id="element-900" />
                      <span className="text-sm font-semibold text-gray-900" data-id="element-901">
                        AI Chat
                      </span>
                    </div>
                    <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-2 text-sm text-gray-800" data-id="element-902">
                      Create vendor "Acme Corp"
                    </div>
                    <div className="bg-orange-500 rounded-2xl rounded-br-sm px-4 py-2 text-sm text-white ml-auto max-w-[80%]" data-id="element-903">
                      Done! Vendor created.
                    </div>
                  </motion.div>}

                {step === 3 && <motion.div key="form" initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} className="bg-white rounded-xl p-6 border border-gray-200 shadow-xl" data-id="element-904">
                    <div className="text-sm font-semibold text-gray-900 mb-4" data-id="element-905">
                      Smart Form
                    </div>
                    <div className="space-y-3" data-id="element-906">
                      <div data-id="element-907">
                        <div className="text-xs text-gray-600 mb-1" data-id="element-908">
                          Company Name
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-sm text-gray-900" data-id="element-909">
                          Acme Corp
                          <span className="text-xs text-green-600 ml-2" data-id="element-910">
                            ✓ Auto-filled
                          </span>
                        </div>
                      </div>
                      <div data-id="element-911">
                        <div className="text-xs text-gray-600 mb-1" data-id="element-912">
                          Category
                        </div>
                        <div className="bg-gray-100 rounded-lg px-3 py-2 text-sm text-gray-400" data-id="element-913">
                          Ask to complete...
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {step === 4 && <motion.div key="success" initial={{
                opacity: 0,
                scale: 0.8
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.8
              }} className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200 shadow-xl" data-id="element-914">
                    <div className="flex items-center gap-3" data-id="element-915">
                      <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center" data-id="element-916">
                        <CheckCircle className="w-7 h-7 text-white" data-id="element-917" />
                      </div>
                      <div data-id="element-918">
                        <div className="text-lg font-bold text-green-900" data-id="element-919">
                          Action Completed
                        </div>
                        <div className="text-sm text-green-700" data-id="element-920">
                          Vendor created successfully
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Bottom Badge */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.5
        }} className="absolute bottom-8 left-16" data-id="element-921">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-2" data-id="element-922">
              <Sparkles className="w-4 h-4 text-orange-500" data-id="element-923" />
              <span className="text-sm font-semibold text-orange-900" data-id="element-924">
                AI-Native UX Components
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
};
export default PreviewImage;